import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Pagination, Rate } from "antd";
import moment from "moment";

import { FaLocationDot } from "react-icons/fa6";
import { FaDollarSign } from "react-icons/fa";

import {
  SortButton,
  Buttons,
  Spacer,
  popover,
  boxContainer,
  inputFields,
  tables,
  checkbox,
  GetProPopup,
} from "../../components";

import { Utils, UtilityArrays } from "../../config";

import { UniversityAction, SearchAction } from "../../store/actions";

import "./styles.css";

const columns = [
  {
    title: "University Name",
    dataIndex: "name",
    key: "1",
    fixed: "left",
    width: "31.25em",
    render: (text, record) => (
      <div className="flex gap-2">
        <div className="w-[15%]">
          <img
            src={record?.profilePic ?? Images.LogoIconOnly}
            className="h-[3.75em] w-[3.75em] border border-searchBorder shadow-boxContainer rounded-full object-contain bg-white"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = Images.LogoIconOnly;
            }}
          />
        </div>

        <div className="flex flex-col gap-2 w-[85%]">
          <div className="font-bold">{record.name}</div>
          <div className="flex gap-2 items-center text-[0.875em]">
            <FaLocationDot />
            <div>
              {`${record?.city ? record?.city : ""}${
                record?.state ? `, ${record?.state}` : ""
              }${record?.country?.name ? `, ${record?.country.name}` : ""}`}
            </div>
          </div>
          <div className="flex gap-2 items-center text-[0.875em]">
            <div className="pr-2 border-r border-r-gray-400">
              Average Fee:{" "}
              {record?.avgFeesUSD ? (
                <popover.WhitePopover
                  content={
                    <div>
                      {record?.avgFeesUSD
                        ? `USD ${record?.avgFeesUSD?.toLocaleString()}`
                        : "NA"}
                    </div>
                  }
                  placement={"top"}
                  trigger="hover"
                >
                  <Rate
                    defaultValue={
                      record?.avgFeesUSD > 0 && record?.avgFeesUSD <= 10000
                        ? 1
                        : record?.avgFeesUSD > 10000 &&
                          record?.avgFeesUSD <= 25000
                        ? 2
                        : record?.avgFeesUSD > 25000 &&
                          record?.avgFeesUSD <= 40000
                        ? 3
                        : record?.avgFeesUSD > 40000 &&
                          record?.avgFeesUSD <= 60000
                        ? 4
                        : 5
                    }
                    character={({ index = 0 }) => customIcons[index + 1]}
                    className="text-main text-[1.25em]"
                    disabled
                  />
                </popover.WhitePopover>
              ) : (
                "NA"
              )}
            </div>
            <div>
              Acceptance Rate:{" "}
              {record?.acceptanceRate
                ? `${(record?.acceptanceRate * 100).toFixed(1)}%`
                : "NA"}
            </div>
          </div>
          <div className="flex gap-2 items-center text-[0.875em]">
            <div className="pr-2 border-r border-r-gray-400">
              Average Course Duration: {record.courseDuration}{" "}
              {record?.courseDuration
                ? record?.courseDuration?.includes("Years")
                  ? null
                  : "Years"
                : "NA"}
            </div>
            <div>
              Application Status:{" "}
              {record?.applicationStatus === "NA" ? (
                "NA"
              ) : (
                <span
                  className={`${
                    record?.applicationStatus === "closed"
                      ? "text-red-500"
                      : "text-green-500"
                  }  font-bold`}
                >
                  {record?.applicationStatus === "closed" ? "CLOSED" : "OPEN"}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: "QS World Ranking",
    dataIndex: "qs",
    key: "2",
    width: 170,
    align: "center",
    render: (text, record) => (
      <div>{record?.rankings?.QS ? `#${record?.rankings?.QS}` : "NA"}</div>
    ),
  },
  {
    title: "Average Fee",
    dataIndex: "avgFee",
    key: "4",
    width: 150,
    align: "center",
    render: (text, record) => (
      <div>
        {record?.avgFeesUSD
          ? `USD ${record?.avgFeesUSD?.toLocaleString()}`
          : "NA"}
      </div>
    ),
  },
  {
    title: "University Type",
    dataIndex: "type",
    key: "5",
    width: 170,
    align: "center",
    render: (text, record) => <div>{record?.Type?.type ?? "NA"}</div>,
  },
  {
    title: "ACT Midrange",
    dataIndex: "actmirange",
    key: "6",
    width: 150,
    align: "center",
    render: (text, record) => (
      <div>{record?.testScores?.ACTMidrange ?? "NA"}</div>
    ),
  },
  {
    title: "SAT Midrange",
    dataIndex: "satmirange",
    key: "7",
    width: 150,
    align: "center",
    render: (text, record) => (
      <div>{record?.testScores?.SATMidrange ?? "NA"}</div>
    ),
  },

  {
    title: "IELTS Requirement",
    dataIndex: "ielts",
    key: "8",
    width: 180,
    align: "center",
    render: (text, record) => <div>{record?.requirements?.IELTS ?? "NA"}</div>,
  },
  {
    title: "TOEFL Requirement",
    dataIndex: "toefl",
    key: "9",
    width: 180,
    align: "center",
    render: (text, record) => <div>{record?.requirements?.TOEFL ?? "NA"}</div>,
  },
  {
    title: "Application Deadline",
    dataIndex: "appDeadline",
    key: "10",
    width: 180,
    align: "center",
    render: (text, record) => (
      <div className="flex flex-col">
        <div className="font-bold rounded-md bg-liteBoxBg border border-main">
          {record?.deadlines?.length > 0
            ? record?.deadlines[0]?.appDeadline
              ? moment(record?.deadlines[0]?.appDeadline).format(
                  "DD MMMM, YYYY"
                )
              : "NA"
            : "NA"}
        </div>
      </div>
    ),
  },
  {
    title: (
      <>
        SAT 25<sup>th</sup> Percentile
      </>
    ),
    dataIndex: "sat25",
    key: "11",
    width: 170,
    align: "center",
    render: (text, record) => (
      <div>{record?.testScores?.SATEnglish25Percentile ?? "NA"}</div>
    ),
  },

  {
    title: (
      <>
        SAT 75<sup>th</sup> Percentile
      </>
    ),
    dataIndex: "sat75",
    key: "12",
    width: 170,
    align: "center",
    render: (text, record) => (
      <div>{record?.testScores?.SATEnglish75Percentile ?? "NA"}</div>
    ),
  },

  {
    title: "Times Ranking",
    dataIndex: "timesRanking",
    key: "13",
    width: 140,
    align: "center",
    render: (text, record) => (
      <div>
        {record?.rankings?.TIMES ? `#${record?.rankings?.TIMES}` : "NA"}
      </div>
    ),
  },
  {
    title: "CWUR Ranking",
    dataIndex: "cwurRanking",
    key: "14",
    width: 140,
    align: "center",
    render: (text, record) => (
      <div>{record?.rankings?.CWUR ? `#${record?.rankings?.CWUR}` : "NA"}</div>
    ),
  },
  {
    title: "% Student Receiving Aid",
    dataIndex: "receiveAid",
    key: "15",
    width: 200,
    align: "center",
    render: (text, record) => (
      <div>
        {record?.percStudentsReceivingAid
          ? `${(parseFloat(record?.percStudentsReceivingAid) * 100).toFixed(
              2
            )}%`
          : "NA"}
      </div>
    ),
  },
  {
    title: "Average Financial Aid Granted Per Year",
    dataIndex: "avgAid",
    key: "16",
    width: 250,
    align: "center",
    render: (text, record) => (
      <div>
        {record?.avgFinancialAid
          ? Utils.numWithCommas(record?.avgFinancialAid)
          : "NA"}
      </div>
    ),
  },
  {
    title: "City Type",
    dataIndex: "cityType",
    key: "17",
    width: 130,
    align: "center",
    render: (text, record) => <div>{record?.CityType?.cityType ?? "NA"}</div>,
  },
  {
    title: "US Region",
    dataIndex: "usRegion",
    key: "18",
    width: 130,
    align: "center",
    render: (text, record) => (
      <div>{record?.country?.name === "USA" ? "Yes" : "No"}</div>
    ),
  },
  {
    title: "Research Output",
    dataIndex: "researchOutput",
    key: "19",
    width: 150,
    align: "center",
    render: (text, record) => <div>{record?.researchOutput ?? "NA"}</div>,
  },
];
const defaultCheckedList = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

const customIcons = {
  1: <FaDollarSign />,
  2: <FaDollarSign />,
  3: <FaDollarSign />,
  4: <FaDollarSign />,
  5: <FaDollarSign />,
};

const ExploreResults = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const profileData = useSelector((state) => state?.profile?.user);
  const { exploreSearch } = useSelector((state) => state.search);
  const { universities, isLoading, count } = useSelector(
    (state) => state.university
  );
  const { countries, programTypes, programs, tags } = useSelector(
    (state) => state.others
  );

  const [checkedList, setCheckedList] = useState(defaultCheckedList);
  const [budgetMin, setBudgetMin] = useState(null);
  const [budgetMax, setBudgetMax] = useState(null);
  const [pageNo, setPageNo] = useState(exploreSearch?.pageNo || 1);
  const [filters, setFilters] = useState({
    limit: exploreSearch?.limit || 10,
    offset: exploreSearch?.offset || 0,
    search: exploreSearch?.search || "",
    region: exploreSearch?.region || [],
    areaOfStudy: exploreSearch?.areaOfStudy || [],
    majors: exploreSearch?.majors || [],
    budget: exploreSearch?.budget || "",
    scholarships: exploreSearch?.scholarships || "",
    tag: exploreSearch?.tag || [],
  });
  const [sortby, setsortby] = useState(exploreSearch?.sortby || "qs");

  const countrylist = countries.map((country) => ({
    label: country.name,
    value: country.id,
  }));
  const programTypeslist = programTypes.map((programType) => ({
    label: programType.programType,
    value: programType.id,
  }));
  const programslist = programs.map((program) => ({
    label: program.programName,
    value: program.id,
  }));
  const tagslist = tags.map((tag) => ({
    label: tag.tagName,
    value: tag.id,
  }));

  const options = columns.map(({ key, title }) => ({
    label: title,
    value: key,
  }));
  const newColumns = columns.map((item) => ({
    ...item,
    hidden: !checkedList?.includes(item.key),
  }));
  newColumns.unshift({
    title: "#",
    dataIndex: "serial",
    key: "01",
    fixed: "left",
    width: 70,
    render: (text, record, index) => (
      <div>{(pageNo - 1) * filters.limit + index + 1}</div>
    ),
  });

  useEffect(() => {
    if (budgetMin || budgetMax) {
      handleFilterChange({
        budget: `${budgetMin}-${budgetMax}`,
      });
    }
  }, [budgetMin, budgetMax]);
  useEffect(() => {
    const budget = exploreSearch?.budget || "";
    const matchingValue = UtilityArrays?.budgetValues.find(
      (item) => item.value === budget
    );

    if (!matchingValue && budget) {
      const [min, max] = budget.split("-").map(Number);
      setBudgetMin(min || null);
      setBudgetMax(max || null);
    } else {
      setBudgetMin(null);
      setBudgetMax(null);
    }
  }, [exploreSearch]);

  useEffect(() => {
    handleSearch();
  }, [exploreSearch]);

  useEffect(() => {
    dispatch(
      SearchAction.exploreSearch({
        ...filters,
        pageNo,
        sortby,
      })
    );
  }, [filters, pageNo, sortby]);

  const handleSearch = async () => {
    const payload = {
      ...exploreSearch,
      region: JSON.stringify(exploreSearch.region),
      majors: JSON.stringify(exploreSearch.majors),
      areaOfStudy: JSON.stringify(exploreSearch.areaOfStudy),
      tag: JSON.stringify(exploreSearch.tag),
    };
    dispatch(UniversityAction.getUniversities(payload));
  };

  const onShowSizeChange = (current, pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      limit: pageSize,
      offset: (current - 1) * pageSize,
    }));
    setPageNo(current);
  };

  const handlePageChange = (pageNumber) => {
    setPageNo(pageNumber);
    setFilters((prevFilters) => ({
      ...prevFilters,
      offset: (pageNumber - 1) * prevFilters.limit,
    }));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleReset = async () => {
    setFilters({
      ...filters,
      offset: 0,
      search: "",
      region: [],
      areaOfStudy: [],
      majors: [],
      budget: "",
      scholarships: "",
      tag: [],
    });
    setPageNo(1);
    setBudgetMin(null);
    setBudgetMax(null);
    setsortby(null);
  };

  const handleFilterChange = (updatedFilter) => {
    setPageNo(1);
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...updatedFilter,
      offset: 0,
    }));
  };

  const handleRemoveFilter = (filterKey, value) => {
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };
      if (Array.isArray(updatedFilters[filterKey])) {
        updatedFilters[filterKey] = updatedFilters[filterKey].filter(
          (item) => item !== value
        );
      } else {
        updatedFilters[filterKey] = "";
      }
      return updatedFilters;
    });
    setPageNo(1);
  };

  const filterButtons = [
    {
      title: "Countries",
      placement: "bottomLeft",
      hoverTitle: "Click to filter universities by country.",
      items: (
        <div className="w-[25em]">
          <inputFields.SelectDropdownMultiple
            items={countrylist}
            onChange={(value) => handleFilterChange({ region: value })}
            value={filters.region}
            allowClear={true}
          />
        </div>
      ),
    },
    {
      title: "Area of Study",
      hoverTitle:
        "Click to explore universities by fields such as Arts & Humanities, Engineering & Technology, and more.",
      items: (
        <div className="w-[25em]">
          <inputFields.SelectDropdownMultiple
            items={programTypeslist}
            onChange={(value) =>
              handleFilterChange({ areaOfStudy: value, majors: [] })
            }
            value={filters.areaOfStudy}
            allowClear={true}
          />
        </div>
      ),
    },
    ...(filters.areaOfStudy.length > 0
      ? [
          {
            title: "Majors",
            hoverTitle: "Click to explore universities by specific majors.",
            items: (
              <div className="w-[25em]">
                <inputFields.SelectDropdownMultiple
                  items={programslist}
                  onChange={(value) => handleFilterChange({ majors: value })}
                  value={filters.majors}
                  allowClear={true}
                />
              </div>
            ),
          },
        ]
      : []),
    {
      title: "Budget",
      hoverTitle:
        "Click to filter universities based on your budget range in USD.",
      items: (
        <div className="filter-dropdown">
          {UtilityArrays?.budgetValues?.map((item, index) => (
            <div
              key={index}
              onClick={() => {
                setBudgetMin(null);
                setBudgetMax(null);
                handleFilterChange({ budget: item.value });
              }}
              className={`${
                filters.budget === item.value
                  ? "active-filter-value-list"
                  : "filter-value-list"
              }`}
            >
              {item.title}
            </div>
          ))}
          <div className="flex flex-col gap2 mt-2">
            <div>Custom</div>
            <div className="flex flex-col items-center">
              <div className="w-[150px]">
                <inputFields.DefaultInput
                  placeholder="Min"
                  type="number"
                  value={budgetMin ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBudgetMin(value ? value : null);
                  }}
                />
              </div>
              to
              <div className="w-[150px]">
                <inputFields.DefaultInput
                  placeholder="Max"
                  type="number"
                  value={budgetMax ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBudgetMax(value ? value : "");
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Scholarships",
      hoverTitle:
        "Click to filter universities offering scholarships or those without available scholarships.",
      items: (
        <div className="filter-dropdown">
          {UtilityArrays?.scholarshipValues?.map((item, index) => (
            <div
              key={index}
              onClick={() => handleFilterChange({ scholarships: item.value })}
              className={`${
                filters.scholarships === item.value
                  ? "active-filter-value-list"
                  : "filter-value-list"
              }`}
            >
              {item.title}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "University Tags",
      hoverTitle:
        "Click to filter universities by tags such as Group of 8, Russell Group, Universitas 21, and more.",
      items: (
        <div className="w-[25em]">
          <inputFields.SelectDropdownMultiple
            items={tagslist}
            onChange={(value) => handleFilterChange({ tag: value })}
            value={filters.tag}
            allowClear={true}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="universities">
      <div className="flex justify-center gap-2">
        <inputFields.DefaultSearchBox
          onChange={(e) => {
            setPageNo(1);
            setFilters({
              ...filters,
              offset: 0,
              search: e.target.value,
            });
          }}
          value={filters.search}
          onKeyDown={handleKeyDown}
        />
        <div className="w-[7.5em]">
          <Buttons.DefaultButton
            size="medium"
            width="full"
            style={{ height: "100%" }}
            onClick={() => handleSearch()}
          >
            Search
          </Buttons.DefaultButton>
        </div>
        <div className="w-[7.5em]">
          <Buttons.RedButton
            style={{ height: "100%" }}
            size="medium"
            width="full"
            onClick={() => navigate("/explore")}
            title="Click to discover and browse more university options."
          >
            Explore
          </Buttons.RedButton>
        </div>
      </div>
      <Spacer height={20} />
      <div className="flex justify-between">
        <div className="flex gap-2">
          {filterButtons?.map((item, index) => (
            <popover.WhitePopover
              content={item.items}
              placement={item?.placement ?? "bottom"}
              key={index}
            >
              <Buttons.DefaultButton size="small" title={item?.hoverTitle}>
                {item?.title}
              </Buttons.DefaultButton>
            </popover.WhitePopover>
          ))}
        </div>
        {Object.values(filters).some(
          (value) => value !== "" && value?.length > 0
        ) && (
          <div className="font-semibold cursor-pointer" onClick={handleReset}>
            Clear Filters
          </div>
        )}
      </div>
      {Object.values(filters).some(
        (value) => value !== "" && value?.length > 0
      ) && (
        <>
          <Spacer height={20} />
          <div>Filters:</div>
          <div className="flex flex-wrap items-center gap-2 py-3">
            {filters.search && (
              <div className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2">
                Search: {filters.search}
                <span
                  className="cursor-pointer text-red-500"
                  onClick={() => handleRemoveFilter("search")}
                >
                  ×
                </span>
              </div>
            )}
            {filters.region.length > 0 &&
              filters.region.map((id) => (
                <div
                  key={id}
                  className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2"
                >
                  Countries: {countrylist.find((c) => c.value === id)?.label}
                  <span
                    className="cursor-pointer text-red-500"
                    onClick={() => handleRemoveFilter("region", id)}
                  >
                    ×
                  </span>
                </div>
              ))}
            {filters.areaOfStudy.length > 0 &&
              filters.areaOfStudy.map((id) => (
                <div
                  key={id}
                  className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2"
                >
                  Area of Study:{" "}
                  {programTypeslist.find((p) => p.value === id)?.label}
                  <span
                    className="cursor-pointer text-red-500"
                    onClick={() => handleRemoveFilter("areaOfStudy", id)}
                  >
                    ×
                  </span>
                </div>
              ))}
            {filters.majors.length > 0 &&
              filters.majors.map((id) => (
                <div
                  key={id}
                  className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2"
                >
                  Majors: {programslist.find((p) => p.value === id)?.label}
                  <span
                    className="cursor-pointer text-red-500"
                    onClick={() => handleRemoveFilter("majors", id)}
                  >
                    ×
                  </span>
                </div>
              ))}
            {filters.budget && (
              <div className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2">
                Budget:{" "}
                {UtilityArrays?.budgetValues.find(
                  (b) => b.value === filters.budget
                )?.title ??
                  (() => {
                    const [min, max] = filters.budget.split("-").map(Number);
                    if (!min && max) return `<${max} $`;
                    if (min && !max) return `>${min} $`;
                    return `${min} - ${max} $`;
                  })()}
                <span
                  className="cursor-pointer text-red-500"
                  onClick={() => handleRemoveFilter("budget")}
                >
                  ×
                </span>
              </div>
            )}
            {filters.scholarships && (
              <div className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2">
                Scholarships:{" "}
                {
                  UtilityArrays?.scholarshipValues.find(
                    (s) => s.value === filters.scholarships
                  )?.title
                }
                <span
                  className="cursor-pointer text-red-500"
                  onClick={() => handleRemoveFilter("scholarships")}
                >
                  ×
                </span>
              </div>
            )}
            {filters.tag.length > 0 &&
              filters.tag.map((id) => (
                <div
                  key={id}
                  className="px-3 py-1 bg-liteBoxBg border border-liteBoxBorder text-black rounded-lg flex items-center gap-2"
                >
                  Tags: {tagslist.find((p) => p.value === id)?.label}
                  <span
                    className="cursor-pointer text-red-500"
                    onClick={() => handleRemoveFilter("tag", id)}
                  >
                    ×
                  </span>
                </div>
              ))}
          </div>
        </>
      )}
      <Spacer height={20} />
      <boxContainer.UniversityBoxContainer>
        <div className="flex justify-between border-b border-b-liteBoxBorder px-4 py-3">
          <div className="flex items-center gap-3 ">
            <div className="font-bold pl-2 text-[1.25em]">
              {count.toLocaleString()} Universities
            </div>
          </div>

          <popover.WhitePopover
            content={
              <checkbox.VerticalCheckbox
                value={checkedList}
                options={options}
                onChange={(value) => {
                  setCheckedList(value);
                }}
              />
            }
          >
            <SortButton title="Click to edit and personalize the displayed data points by adding or removing them as needed." />
          </popover.WhitePopover>
        </div>
        <div className="w-[calc(100vw-20em)] overflow-x-auto max-w-full relative">
          {profileData?.freeUser && (
            <div className="absolute top-[400px] left-[35%] z-[10]">
              <GetProPopup />
            </div>
          )}
          <tables.DefaultTable
            columns={newColumns}
            data={universities}
            borderradius={0}
            rowKey={() => Math.random()}
            loading={isLoading}
            onRow={(record, index) => ({
              onClick: (event) => {
                if (event.ctrlKey) {
                  window.open(`/universities/${record.id}`, "_blank");
                } else {
                  navigate(`/universities/${record.id}`);
                }
              },
              style:
                profileData?.freeUser && index !== 0
                  ? { filter: "blur(14px)", pointerEvents: "none" }
                  : {},
            })}
          />
        </div>
        {!profileData?.freeUser && (
          <div className="flex justify-center items-center p-3">
            <Pagination
              responsive
              hideOnSinglePage={false}
              current={pageNo}
              pageSize={filters.limit}
              total={count}
              showSizeChanger
              pageSizeOptions={UtilityArrays?.pageSizes}
              onShowSizeChange={onShowSizeChange}
              onChange={handlePageChange}
            />
          </div>
        )}
      </boxContainer.UniversityBoxContainer>
    </div>
  );
};

export default ExploreResults;
